import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import authService from '../utils/login/AuthService';
import { isSsoEnabled } from '../utils/login/oidc';

// 白名单路由，不需要登录即可访问
const whiteList = ['/callBack', '/callback', '/login', '/logout'];

/**
 * 路由守卫组件
 * 1. 如果存在 access_token，直接放行
 * 2. 如果不存在 access_token，检查是否在白名单中
 * 3. 如果不在白名单中，调用 AuthService.login() 发起登录
 */
const AuthGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  
  // 使用 state 来存储 token，这样可以响应 localStorage 的变化
  const [accessToken, setAccessToken] = useState(() => {
    return (
      localStorage.getItem('access_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('accessToken')
    );
  });

  // 跟踪是否正在发起登录，避免重复调用
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // 检查当前路径是否在白名单中
  const isInWhiteList = whiteList.some(
    path => location.pathname === path || location.pathname.startsWith(path + '/')
  );

  // 监听 localStorage 的变化
  useEffect(() => {
    const checkToken = () => {
      const token = (
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        localStorage.getItem('accessToken')
      );
      setAccessToken(token);
    };

    // 初始检查
    checkToken();

    // 监听 storage 事件（当其他标签页或窗口修改 localStorage 时）
    window.addEventListener('storage', checkToken);
    
    // 监听自定义事件（当同窗口内修改 localStorage 时触发）
    const handleTokenUpdate = () => {
      checkToken();
    };
    window.addEventListener('tokenUpdated', handleTokenUpdate);

    // 定期检查 token（作为兜底方案，处理直接修改 localStorage 的情况）
    // 使用较长的间隔，避免过于频繁的检查（改为 2 秒）
    const interval = setInterval(checkToken, 2000);

    return () => {
      window.removeEventListener('storage', checkToken);
      window.removeEventListener('tokenUpdated', handleTokenUpdate);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    // 如果存在 token，直接放行（不需要做任何处理）
    if (accessToken) {
      setIsLoggingIn(false);
      return;
    }
    // 如果不存在 token，检查当前路径是否在白名单中
    if (isInWhiteList) {
      // 在白名单中，允许访问
      setIsLoggingIn(false);
      return;
    }

    // 如果已经在登录流程中，避免重复调用
    if (isLoggingIn) {
      return;
    }

    // 如果不在白名单中且没有 token，调用 AuthService.login() 发起登录
    // AuthService.login() 会进行 OIDC 重定向，保存当前路径到 state 中
    const initiateLogin = async () => {
      setIsLoggingIn(true);
      try {
        if (!isSsoEnabled) {
          // 关闭单点时，直接走普通登录页（不做 OIDC 重定向）
          navigate('/login', {
            replace: true,
            state: { from: location },
          });
          return;
        }

        await authService.login();
        // login() 方法会进行重定向，所以这里不需要 navigate
      } catch (error) {
        console.error('登录失败:', error);
        setIsLoggingIn(false);
        // 如果登录失败，降级到普通登录页
        navigate('/login', {
          replace: true,
          state: { from: location },
        });
      }
    };

    // 添加短暂延迟，避免在 Callback 页面设置 token 的瞬间触发
    const timer = setTimeout(() => {
      // 再次检查 token（可能在延迟期间已经设置了）
      const currentToken = localStorage.getItem('access_token') || 
                          localStorage.getItem('token') || 
                          localStorage.getItem('accessToken');
      if (!currentToken) {
        initiateLogin();
      } else {
        setAccessToken(currentToken);
        setIsLoggingIn(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [accessToken, isInWhiteList, isLoggingIn, navigate, location]);

  // 如果存在 token 或在白名单中，渲染子路由
  if (accessToken || isInWhiteList) {
    return <Outlet />;
  }

  // 否则显示加载中（正在发起登录或跳转到登录页）
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: '16px',
      }}
    >
      <div style={{ fontSize: '16px', color: '#666' }}>
        {isLoggingIn ? '正在跳转到登录页面...' : '加载中...'}
      </div>
    </div>
  );
};

export default AuthGuard;

